import { useCallback, useEffect, useMemo, useState } from 'react';

import { getInvitationByToken, validateApiResponse } from '../../../services';
import { LINK_STATE } from '../constants/invitationStates.js';
import { getInvitationLinkState } from '../utils/getInvitationLinkState.js';

const mapApiInvitationToUi = (invitation) => ({
  status: invitation.status,
  workspaceName: invitation.workspace_name ?? invitation.workspaceName,
  invitedEmail: invitation.invited_email ?? invitation.invitedEmail ?? null,
  welcomeMessage: invitation.welcome_message ?? invitation.welcomeMessage ?? null,
  expiresAt: invitation.expires_at ?? invitation.expiresAt ?? null,
});

const LOADING = { invitation: null, linkState: null, loading: true, error: null };

/**
 * Loads the public details of the invitation behind `token` and says which screen it gets.
 * A 404 is a normal outcome (`invalid`); any other failure is returned as `error`.
 *
 * @param {string} token
 * @param {{ signedInEmail?: string|null }} [options] Email of the session, when there is one: a
 *   valid link meant for someone else becomes `otherAccount`.
 */
export const useInvitation = (token, { signedInEmail } = {}) => {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ key: null });
  const key = `${token}:${attempt}`;
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let mounted = true;
    getInvitationByToken(token)
      .then((res) => {
        if (!mounted) return;
        if (res.status_code === 404) {
          setResult({ key, invitation: null, linkState: LINK_STATE.INVALID, loading: false, error: null });
          return;
        }
        const raw = validateApiResponse(res).body;
        setResult({
          key,
          invitation: mapApiInvitationToUi(raw),
          raw,
          linkState: getInvitationLinkState(raw),
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        if (mounted) setResult({ key, invitation: null, linkState: null, loading: false, error });
      });
    return () => {
      mounted = false;
    };
  }, [token, key]);

  // A result that belongs to another token or attempt is stale: report loading until the new one lands.
  return useMemo(() => {
    const current = result.key === key ? result : LOADING;
    const linkState = current.raw
      ? getInvitationLinkState(current.raw, new Date(), { signedInEmail })
      : current.linkState;
    return { ...current, linkState, retry };
  }, [result, key, retry, signedInEmail]);
};
